<template>
  <v-slide-x-transition mode="out-in" :class="compact && 'elevation-0 pa-0'">
    <v-card v-if="loading">
      <v-skeleton-loader type="card" />
    </v-card>
    <v-card
      v-else-if="donation && donation.paymentSpecification"
      :class="compact && 'elevation-0 pa-0'"
    >
    <div class="d-flex justify-space-between align-center mb-8">
      <v-card-title :class="compact && 'pa-0 text-gray-900'" class="donation-flow__step-title" style="padding-top: 0px !important;">
        {{ t('donationGuide.summaryView.title') }}
      </v-card-title>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 3, total: 3 }) }}</span>
    </div>
    <option-label
      :title="t('donationGuide.payment.paymentTitle')"
      class="mb-8"
      />
      <v-card-text
        :class="compact && 'pl-0 pt-0'"
        :style="{ padding: compact ? '0 !important' : undefined }"
      >
        <p>{{ t('payment.text') }}</p>
        <v-form ref="form" v-model="valid" class="mb-3">
          <v-alert
            v-if="failed"
            data-cy="payment-view-alert"
            class="mb-4"
            density="compact"
            variant="outlined"
            type="error"
          >
            <p v-if="errorText" class="mb-0">
              {{ errorText }}
            </p>
            <p v-else class="mb-0">
              {{ t('payment.failedText') }}
            </p>
          </v-alert>
          <payment-method-select-component
            v-model="donation.paymentSpecification.paymentMethod"
            :payment-methods="paymentMethodsForSelect"
            data-cy="payment-method-select-component"
            hide-divider
          />
          <v-slide-y-transition>
            <div v-if="isInvoicePayment(donation.paymentSpecification.paymentMethod)">
              <v-row class="personal-info-fields">
                <v-col cols="12" sm="6" class="py-0">
                  <option-label
                    :title="
                      (t('donorDetails.form.firstname.yourName') ||
                        t('donorDetails.form.firstname.label')) + '*'
                    "
                    class="mb-1"
                  />
                  <v-text-field
                    v-model="donation.donor.firstname"
                    :placeholder="t('donorDetails.form.firstname.placeholder')"
                    persistent-placeholder
                    data-cy="paymentview-firstname-field"
                    :rules="[rules.requiredField(t('donorDetails.form.firstname.label'))]"
                    class="mb-3"
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="6" class="py-0">
                  <option-label
                    :title="
                      (t('donorDetails.form.lastname.yourSurname') ||
                        t('donorDetails.form.lastname.label')) + '*'
                    "
                    class="mb-1"
                  />
                  <v-text-field
                    v-model="donation.donor.surname"
                    :placeholder="t('donorDetails.form.lastname.placeholder')"
                    persistent-placeholder
                    data-cy="paymentview-surname-field"
                    :rules="[rules.requiredField(t('donorDetails.form.lastname.label'))]"
                    class="mb-3"
                    clearable
                  />
                </v-col>
              </v-row>
              <option-label
                :title="
                  (t('donorDetails.form.email.yourEmail') || t('donorDetails.form.email.label')) + '*'
                "
                class="mb-1"
              />
              <v-text-field
                v-model="donation.donor.email"
                :placeholder="t('donorDetails.form.email.placeholder')"
                persistent-placeholder
                data-cy="paymentview-email-field"
                :rules="[rules.email, rules.requiredField(t('donorDetails.form.email.label'))]"
                :validate-on-blur="true"
                class="mb-3"
                type="email"
                clearable
                @keydown.space.prevent
              />
              <div v-if="isEmailInvoice">
                <option-label :title="t('donorDetails.form.invoice.label') + '*'" class="mb-1" />
                <v-text-field
                  v-model="invoiceEmail"
                  :hint="t('donationDetails.form.paymentMethod.invoiceEmailHint')"
                  :persistent-hint="true"
                  :rules="[rules.email, rules.required]"
                  :validate-on-blur="true"
                  class="mb-3"
                  data-cy="paymentview-invoice-email"
                  required
                  type="email"
                  @keydown.space.prevent
                />
              </div>
              <option-label :title="t('donorDetails.form.address.label') + '*'" class="mb-1" />
              <v-text-field
                v-model="donation.donor.address.address1"
                :hint="t('donorDetails.form.address.hint')"
                :placeholder="t('donorDetails.form.address.placeholder')"
                persistent-placeholder
                data-cy="paymentview-address1-field"
                :rules="[rules.requiredField(t('donorDetails.form.address.label'))]"
                class="mb-3"
              />
              <option-label :title="t('donorDetails.form.postcode.label') + '*'" class="mb-1" />
              <v-text-field
                v-model="donation.donor.address.postcode"
                :hint="t('donorDetails.form.postcode.hint')"
                :placeholder="t('donorDetails.form.postcode.placeholder')"
                persistent-placeholder
                data-cy="paymentview-postcode-field"
                :rules="[rules.requiredField(t('donorDetails.form.postcode.label'))]"
                class="mb-3"
              />
              <option-label :title="t('donorDetails.form.city.label') + '*'" class="mb-1" />
              <v-text-field
                v-model="donation.donor.address.city"
                :hint="t('donorDetails.form.city.hint')"
                :placeholder="t('donorDetails.form.city.placeholder')"
                persistent-placeholder
                data-cy="paymentview-city-field"
                :rules="[rules.requiredField(t('donorDetails.form.city.label'))]"
                class="mb-3"
              />
              <option-label :title="t('donorDetails.form.country.label')" class="mb-1" />
              <v-text-field
                v-model="donation.donor.address.country"
                :hint="t('donorDetails.form.country.hint')"
                :placeholder="t('donorDetails.form.country.placeholder')"
                persistent-placeholder
                data-cy="paymentview-country-field"
                class="mb-3"
              />
            </div>
          </v-slide-y-transition>
          <v-slide-y-transition>
            <div>
              <option-label
                v-if="donation.paymentSpecification?.paymentMethod?.type === PaymentType.SWISH ||
                      donation.paymentSpecification?.paymentMethod?.type === PaymentType.PAYLINK_SMS"
                :title="mobileLabel + '*'"
                class="mb-1"
              />
              <v-text-field
                v-if="donation.paymentSpecification?.paymentMethod?.type === PaymentType.SWISH ||
                      donation.paymentSpecification?.paymentMethod?.type === PaymentType.PAYLINK_SMS"
                v-model="mobileNumber"
                :hint="t('donationDetails.form.mobileNumber.hint')"
                :placeholder="t('donorDetails.form.mobileNumber.label')"
                :persistent-hint="true"
                :rules="[rules.mobilePhoneNumber, rules.requiredField(mobileLabel)]"
                :validate-on-blur="true"
                class="mb-3 mt-0"
                data-cy="paymentview-telephone-number"
                required
                @keydown.space.prevent
              />
            </div>
          </v-slide-y-transition>
          <option-label
            :title="t('donationGuide.payment.donationAmountLabel')"
            class="mb-2 mt-2"
          />
          <v-slide-y-transition>
            <v-card class="mt-2 mb-5" flat>
              <donation-amount-component
                v-model="donationAmountValue"
                :admin-fee="donation.paymentSpecification.adminAmount"
                :min="donationSettings.min"
                :max="donationSettings.max"
                :display-fee="!adminPercentage"
                :read-only="true"
                :rules="[rules.required, validateDonationAmount]"
                data-cy="paymentview-donation-amount-component"
                hide-label
              />
              <div v-if="adminAmount > 0" class="donation-summary pb-4">
                <div class="donation-summary__row">
                  <span class="donation-summary__label">
                    {{ t('donationGuide.payment.summary.fee') }}
                    <v-tooltip location="top" :open-delay="0" :close-delay="1000" :max-width="300" color="white">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" size="small" class="ml-1 fee-info-icon">mdi-information</v-icon>
                      </template>
                      <span>{{ t('donationDetails.form.adminFee.hint') }}</span>
                    </v-tooltip>
                  </span>
                  <span class="donation-summary__value">+ {{ formatCurrency(adminAmount) }}</span>
                </div>
                <v-divider class="my-6" />
                <div class="donation-summary__row donation-summary__row--total mt-4">
                  <span class="donation-summary__label">{{
                    t('donationGuide.payment.summary.total')
                  }}</span>
                  <span class="donation-summary__value">{{ formatCurrency(totalAmountCalculated) }}</span>
                </div>
              </div>
            </v-card>
          </v-slide-y-transition>
        </v-form>
        <!-- <tax-reduction-information-component /> -->
        <paylink-error-retry-component
          :donation="donation"
          :show="sms.errorDialog"
          :message="sms.errorMessage"
          @to-payment-selection="goToPaymentMethodSelection"
          @to-success="goToSuccessMethodSelection"
        />
      </v-card-text>
      <donation-flow-buttons
        previous-id="paymentViewResetButton"
        next-id="paymentViewGoToPaymentButton"
        previous-data-cy="payment-method-reset-btn"
        next-data-cy="payment-method-goto-payment-btn"
        :previous-label="t('payment.reset')"
        :previous-aria-label="t('aria.label.reset')"
        :next-label="paymentText"
        :next-aria-label="t('aria.label.pay')"
        :next-loading="loading"
        :next-disabled="loading"
        @previous-step="reset"
        @next-step="goToPayment()"
      />
      <v-card-text class="mt-4 policy-text">
        {{ t('legal.policyText.intro') }}
        <a :href="privacyPolicyUrl" target="_blank" rel="noopener">{{ t('legal.policyText.privacyPolicyLink') }}</a>.
      </v-card-text>
    </v-card>
  </v-slide-x-transition>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import store from '@/store';
import { scrollToFirstInvalid } from '@/utils/formHelpers';
import { useLabel } from '@/composables/useLabel';
import { useValidationRules } from '@/composables/useValidationRules';
import { ENDPOINTS, get, put, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { useInvoiceOperations } from '@/composables/useInvoiceOperations';
import { useAdminFee } from '@/composables/useAdminFee';
import { useSmsOperations } from '@/composables/useSmsOperations';
import { usePaymentMethods } from '@/composables/usePaymentMethods';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import PaymentMethodSelectComponent from '@/components/payment/PaymentMethodSelectComponent.vue';
import { isInvoice, isPayLink } from '@/classes/PaymentMethod';
import { PaymentMethod, Type as PaymentType } from '@/interfaces/economy/paymentMethod';
import { Invoice } from '@/interfaces/economy/invoice';
import DonationAmountComponent from '@/components/payment/DonationAmountComponent.vue';
import TaxReductionInformationComponent from '@/components/taxreduction/TaxReductionInfoComponent.vue';
import { Sms } from '@/interfaces/sms';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import PaylinkErrorRetryComponent from '@/components/payment/PaylinkErrorRetryComponent.vue';
import { SmsType } from '@/enums/smsType';
import { AdminFeeResponse } from '@/interfaces/economy/adminFeeResponse';
import { Intermediary } from '@/interfaces/intermediary/intermediary';
import { getDonationSettings } from '@/utils/donationSettings';
import { getCurrencyForLocation } from '@/utils/currency';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import MobileDetect from 'mobile-detect';
import OptionLabel from '@/components/OptionLabel.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import matomo from '@/services/matomoService';

export default defineComponent({
  name: 'PaymentView',
  components: {
    PaymentMethodSelectComponent,
    DonationAmountComponent,
    TaxReductionInformationComponent,
    PaylinkErrorRetryComponent,
    OptionLabel,
    DonationFlowButtons,
  },
  props: {
    compact: Boolean,
  },
  emits: ['reset', 'step', 'toSuccess'],
  setup(props, { emit }) {
    // instance removed — not required
    const { t } = useLabel();
    const { rules: baseRules } = useValidationRules(t);
    const { createInvoice } = useInvoiceOperations();
    const { calculateAdminFee } = useAdminFee();
    const { sendPaymentRequestSms, getLatestSmsStatus } = useSmsOperations();
    const { fetchPaymentMethods } = usePaymentMethods();

    const rules = {
      required: baseRules.value.required,
      email: baseRules.value.email,
      mobilePhoneNumber: baseRules.value.mobilePhoneNumber,
      // label may be a TranslateResult from "t - stringify to avoid TS mismatches in templates
      requiredField: (label) => (v) => !!v || t('validation.required', { field: String(label) }),
    };

    const form = ref();
    const redirecting = ref(false);
    const loading = ref(false);
    const valid = ref(false);
    const failed = ref(false);
    const errorText = ref(null);
    const invoiceEmail = ref('');
    const mobileNumber = ref('');
    const paymentText = ref('');
    const sms = ref({
      errorDialog: false,
      errorMessage: '',
      smsValidationWaitTime: 4000,
    });
    const paymentLoopBreakerThreshold = ref(10);
    const numberOfPaymentAttempts = ref(0);
    const adminPercentage = ref<number | null>(null);
    const amountValue = ref(0);
    const defaultDonationGuideVersion = DonationGuideVersion.V1;
    const localPaymentMethods = ref<PaymentMethod[]>([]);
    const getDonationStepFlowVersion = () =>
      store.state.donationGuideVersion || defaultDonationGuideVersion;

    const currentStep = computed(() => store.state.currentStep);
    watch(currentStep, (step) => {
      if (step === steps.value.PAYMENT) {
        matomo.trackEvent('Donation Payment', 'Viewed', 'Payment Screen');
      }
    });

    onMounted(() => {
      if (!requestSession.value?.paymentMethods?.length) {
        fetchPaymentMethodsLocal();
      }
    });

    const steps = computed(() =>
      getDonationStepFlowVersion() === DonationGuideVersion.V1 ? stepV1 : stepV2
    );
    const currentCurrency = computed(() =>
      getCurrencyForLocation(
        donation.value?.paymentSpecification?.country ?? store.state.userLocation ?? 'SWEDEN'
      )
    );
    const donationSettings = computed(() =>
      getDonationSettings(store.state.userLocation ?? 'SWEDEN')
    );
    const requestSession = computed((): RequestSession => store.state.requestSession!);
    const donation = computed((): Donation => store.state.donation!);
    const paymentMethodsForSelect = computed(() => {
      const methods = requestSession.value?.paymentMethods?.length
        ? requestSession.value.paymentMethods
        : localPaymentMethods.value;
      return methods;
    });
    const isEmailInvoice = computed(
      () =>
        !!donation.value &&
        !!donation.value.paymentSpecification &&
        !!donation.value.paymentSpecification.paymentMethod &&
        donation.value.paymentSpecification.paymentMethod.type === PaymentType.INVOICE_EMAIL
    );
    const nextText = computed(() => {
      return t('payment.pay');
    });
    const mobileLabel = computed(() => {
      if (
        donation.value &&
        donation.value.paymentSpecification &&
        donation.value.paymentSpecification.paymentMethod &&
        donation.value.paymentSpecification.paymentMethod.type === PaymentType.PAYLINK_SMS
      ) {
        return t('payment.customerMobileForPayLink').toString();
      } else {
        if (
          donation.value &&
          (donation.value.incomingChannel === 'Bitnet' ||
            donation.value.incomingChannel === 'Bitnet - förmedling')
        ) {
          return t('payment.customerMobileForSwish').toString();
        } else {
          return t('payment.yourMobileForSwish').toString();
        }
      }
    });
    const isPhoneBased = computed(() => {
      return (
        !!donation.value?.paymentSpecification &&
        !!donation.value?.paymentSpecification.paymentMethod &&
        (donation.value.paymentSpecification.paymentMethod.type === PaymentType.SWISH ||
          donation.value.paymentSpecification.paymentMethod.type === PaymentType.PAYLINK_SMS)
      );
    });
    const donationAmountValue = computed(() => {
      // If we are using the percentage model, we need to show the original cause amount (admin fee is hidden). The amount cannot be changed here.
      if (!donation.value?.paymentSpecification) return 0;
      return !!adminPercentage.value && !!donation.value.paymentSpecification.originalCauseAmount
        ? donation.value.paymentSpecification.originalCauseAmount
        : donation.value.paymentSpecification.causeAmount || 0;
    });

    const adminAmount = computed(() => donation.value?.paymentSpecification?.adminAmount ?? 0);

    const totalAmountCalculated = computed(() => {
      return (donationAmountValue.value || 0) + (adminAmount.value || 0);
    });

    const formatCurrency = (amount: number | null) => {
      if (amount === null || amount === undefined) return '';
      const currency =
        donation.value?.paymentSpecification?.currencyCode ||
        getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    watch(requestSession, (newSession) => {
      if (newSession && !newSession.paymentMethods?.length && !localPaymentMethods.value.length) {
        fetchPaymentMethodsLocal();
      }
    });

    const reset = () => {
      matomo.trackEvent('Donation Payment', 'Clicked', 'Cancel Button');
      emit('reset');
    };

    const goToPaymentMethodSelection = () => {
      sms.value.errorDialog = false;
    };

    const goToSuccessMethodSelection = () => {
      emit('step', steps.value.SUCCESS);
      sms.value.errorDialog = false;
    };

    const isInvoicePayment = (paymentMethodType) => {
      return isInvoice(paymentMethodType.type);
    };

    const isPayLinkPayment = (paymentMethod: PaymentMethod | null) => {
      if (!paymentMethod) {
        return false;
      }
      return isPayLink(paymentMethod.type);
    };

    const validateDonationAmount = (value) => {
      const donationAmount = parseInt(value);
      const trueDonationAmount = parseFloat(value);
      if (typeof donationAmount != 'number') {
        return t('validation.numeric');
      } else if (!Number.isInteger(trueDonationAmount)) {
        return t('validation.nonInteger');
      } else if (/^\d+\.\d+$/.test(value)) {
        // if the value is an integer, but of a bad form (e.g. 10.0)
        return t('validation.hasDecimal');
      } else if (donationSettings.value.max < donationAmount) {
        return t('validation.maxValue', { max: donationSettings.value.max });
      } else if (donationAmount < donationSettings.value.min) {
        return t('validation.minValue', { min: donationSettings.value.min });
      }
      return true;
    };

    const getAdminFee = async () => {
      const donationVal = donation.value;
      if (!donationVal?.paymentSpecification?.paymentMethod) {
        return;
      }

      if (!donationVal.donationCause?.id || !donationVal.donationType?.id) {
        return;
      }

      const intermediary: Intermediary | null = requestSession.value?.intermediary || null;
      const deliveryId = donationVal.deliveryMethod?.id || null;
      // PaymentSpecification doesn't include a 'currency' field in our types; prefer paymentSpecification.country if present
      const currency = donationVal.paymentSpecification?.country
        ? getCurrencyForLocation(donationVal.paymentSpecification.country)
        : getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
      try {
        const data: AdminFeeResponse = await calculateAdminFee(
          donationVal.donationCause.id,
          donationVal.donationType.id,
          {
            deliveryMethodId: deliveryId,
            paymentMethodId: donationVal.paymentSpecification.paymentMethod.id,
            intermediaryId: intermediary?.id || null,
            currency: currency,
          }
        );
        if (!data) return;
        if (data.adminAmount) {
          donationVal.paymentSpecification.adminAmount = data.adminAmount;
          adminPercentage.value = null;
        } else {
          adminPercentage.value = data.adminPercentage;
          const causeAmountForAdminCalculation =
            donationVal.paymentSpecification.originalCauseAmount ||
            donationVal.paymentSpecification.causeAmount ||
            0;
          donationVal.paymentSpecification.adminAmount =
            causeAmountForAdminCalculation * (adminPercentage.value || 0);
        }
      } catch {
        // Admin fee is optional — if the API fails, continue without showing error to user
        // Set admin fee to 0 if the call fails so the flow can continue
        if (donationVal.paymentSpecification) {
          donationVal.paymentSpecification.adminAmount = 0;
        }
      }
    };

    const fetchPaymentMethodsLocal = async () => {
      try {
        const data = await fetchPaymentMethods();
        localPaymentMethods.value = data;
      } catch {
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Failed to fetch payment methods',
        });
      }
    };

    const privacyPolicyUrl = computed(() => {
      const location = store.state.userLocation || 'SWEDEN';
      let langCode = 'se';
      if (location == 'NORWAY') langCode = 'no';
      else if (location == 'GERMANY') langCode = 'de';
      return `${import.meta.env.BASE_URL}privacy/rapid_data_privacy_policy_${langCode}.pdf`;
    });
    const termsUrl = computed(() => {
      const location = store.state.userLocation || 'SWEDEN';
      let langCode = 'se';
      if (location == 'NORWAY') langCode = 'no';
      else if (location == 'GERMANY') langCode = 'de';
      return `${import.meta.env.BASE_URL}privacy/rapid_data_privacy_policy_${langCode}.pdf`;
    });

    const goToPayment = async (isRetry = false) => {
      matomo.trackEvent('Donation Payment', 'Clicked', 'Pay Button');
      errorText.value = null;

      // Only validate form on the user-initiated click, not on recursive retries
      // (the form is hidden by the skeleton loader once loading=true).
      if (!isRetry) {
        if (!form.value) return;
        const { valid: formValid } = await form.value.validate();
        if (!formValid) {
          try {
            scrollToFirstInvalid(form.value, null);
          } catch {
            // no-op
          }
          return;
        }
        numberOfPaymentAttempts.value = 0;
        failed.value = false;
        loading.value = true;
      }

      // ensure donation & payment method are ready
      if (
        !donation.value ||
        !donation.value.paymentSpecification ||
        !donation.value.paymentSpecification.paymentMethod
      ) {
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'Cannot proceed to payment: donation or payment method not ready',
        });
        loading.value = false;
        failed.value = true;
        return;
      }

      // alter the payment method
      const donationVal = donation.value as Donation;
      const pm = donationVal.paymentSpecification.paymentMethod as PaymentMethod;
      put(
        ENDPOINTS.DONATIONS +
          '/' +
          donation.value.serialnumber +
          '/' +
          ENDPOINTS.PAYMENT_METHODS +
          '/' +
          donation.value.paymentSpecification.paymentMethod.id,
        {}
      )
        .then(() => {
          // 1. create invoice
          if (isInvoice(pm.type)) {
            if (invoiceEmail.value.length > 0) {
              donationVal.donor.invoiceEmail = invoiceEmail.value;
            }
            store.commit('updateDonation', donationVal);
            // Update the donation object on the backend to ensure address is saved for postal invoices
            put(ENDPOINTS.DONATIONS + '/' + donationVal.id, donationVal).then(() => {
              createInvoice({
                donationId: donationVal.id,
                invoiceEmail: invoiceEmail.value,
              })
                .then((data: Invoice) => {
                  // go to success view
                  if (data) {
                    setTimeout(() => {
                      emit('step', steps.value.SUCCESS);
                    }, 200);
                  }
                })
                .catch((error) => {
                  loading.value = false;
                  failed.value = true;
                  window.log?.error('[Guide] payment.statusPoll — Error while polling payment status', {
                    message: guideErrorMessage(error),
                    httpStatus: guideHttpStatus(error),
                    serialnumber: donation.value?.serialnumber,
                  });
                  store.commit('setSnackbar', {
                    active: true,
                    color: 'error',
                    text: 'An unexpected error occurred while creating invoice',
                  });
                })
                .finally(() => {
                  loading.value = false;
                });
            });
          }
          // 2. PAYLINK
          else if (isPayLinkPayment(pm)) {
            donationVal.donor.mobileNumber = mobileNumber.value;
            // send the SMS
            sendPaymentRequestSms(donationVal.serialnumber, mobileNumber.value)
              // If we were able to send it, then check if there is an error
              .then(() => {
                setTimeout(() => {
                  getLatestSmsStatus(donationVal.serialnumber)
                    .then((smsRes: Sms) => {
                      if (smsRes.errorMessage && smsRes.errorMessage.length > 0) {
                        const snackbar: Snackbar = {
                          active: true,
                          color: 'error',
                          text: t('payLinkSend.messages.payLinkSend.error').toString(),
                          timeout: 10000,
                          action: null,
                        };
                        sms.value.errorMessage = smsRes.errorMessage;
                        sms.value.errorDialog = true;
                        store.commit('setSnackbar', snackbar);
                        loading.value = false;
                      } else {
                        loading.value = false;
                        emit('step', steps.value.SUCCESS);
                      }
                    })
                    .catch(() => {
                      const snackbar: Snackbar = {
                        active: true,
                        color: 'error',
                        text: t('payLinkSend.messages.payLinkSend.error').toString(),
                        timeout: 10000,
                        action: null,
                      };
                      store.commit('setSnackbar', snackbar);
                      loading.value = false;
                      sms.value.errorMessage = t(
                        'payLinkSend.messages.payLinkSend.errorCheckPhoneNumber'
                      ).toString();
                      sms.value.errorDialog = true;
                    });
                }, sms.value.smsValidationWaitTime);
              })
              .catch(() => {
                const snackbar: Snackbar = {
                  active: true,
                  color: 'error',
                  text: t('payLinkSend.messages.payLinkSend.error').toString(),
                  timeout: 10000,
                  action: null,
                };
                store.commit('setSnackbar', snackbar);
                loading.value = false;
                sms.value.errorMessage = t(
                  'payLinkSend.messages.payLinkSend.probableErrorCheckPhoneNumber'
                ).toString();
                sms.value.errorDialog = true;
              });
          }
          // 3. create digital payment (Default)
          else {
            get(ENDPOINTS.PAYMENTS + '/' + ENDPOINTS.TRANSACTIONS, {
              paymentMethodId: pm.id,
              donationId: donationVal.id,
            })
              .then((response) => {
                // If we get not found or internal server error
                if (response.status && (response.status == 404 || response.status == 500)) {
                  // show error
                  loading.value = false;
                  failed.value = true;
                } else {
                  // else, if we can redirect -> do it
                  if (response.redirectUrl) {
                    matomo.navigateAfterTrack(response.redirectUrl);
                  } else {
                    if (numberOfPaymentAttempts.value > paymentLoopBreakerThreshold.value) {
                      failed.value = true;
                      loading.value = false;
                      store.commit('setSnackbar', {
                        active: true,
                        color: 'error',
                        text: t('payment.failedText').toString(),
                        timeout: 10000,
                        action: null,
                      });
                    } else {
                      // otherwise the previous transaction might have failed and we need to create a new one
                      numberOfPaymentAttempts.value++;
                      goToPayment(true);
                    }
                  }
                }
              })
              .catch((error) => {
                window.log?.error('[Guide] payment.process — Unexpected error while processing payment', {
                  message: guideErrorMessage(error),
                  httpStatus: guideHttpStatus(error),
                  serialnumber: donation.value?.serialnumber,
                });
                store.commit('setSnackbar', {
                  active: true,
                  color: 'error',
                  text: 'An unexpected error occurred while processing payment',
                });
                loading.value = false;
                failed.value = true;
              });
          }
        })
        .catch((error) => {
          window.log?.error('[Guide] payment.init — Failed to initialize payment step', {
            message: guideErrorMessage(error),
            httpStatus: guideHttpStatus(error),
            serialnumber: donation.value?.serialnumber,
          });
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'An unexpected error occurred while updating payment method',
          });
          loading.value = false;
          failed.value = true;
          if (
            error.response?.status === 403
          ) // forbidden means we need to show an error to the user
          {
            errorText.value = error.response.data.msg ? error.response.data.msg : null;
          }
        });
    };

    watch(
      () => donation.value?.paymentSpecification?.paymentMethod,
      (after: PaymentMethod | null | undefined, before: PaymentMethod | null | undefined) => {
        if (after && before && after.type !== before.type) {
          matomo.trackEvent('Donation Payment', 'Changed', 'Payment Method');
          getAdminFee();
          if (isInvoicePayment(after)) {
            const isEmailInvoiceValue = after.type === PaymentType.INVOICE_EMAIL;
            if (isEmailInvoiceValue && donation.value) {
              invoiceEmail.value = donation.value.donor.email;
            }
            paymentText.value = t('confirm.invoice').toString();
          } else if (isPayLinkPayment(after) && donation.value) {
            mobileNumber.value = donation.value.donor.mobileNumber;
            paymentText.value = t('confirm.paylink').toString();
          } else {
            paymentText.value = t('summary.proceedToPayment').toString();
          }
        }
      },
      { deep: true }
    );

    watch(donation, (val: Donation | null) => {
      if (val && val.paymentSpecification) {
        if (isInvoicePayment(val.paymentSpecification.paymentMethod)) {
          paymentText.value = t('confirm.invoice').toString();
        } else if (isPayLinkPayment(val.paymentSpecification.paymentMethod)) {
          mobileNumber.value = val.donor.mobileNumber;
          paymentText.value = t('confirm.paylink').toString();
        } else {
          paymentText.value = t('summary.proceedToPayment').toString();
        }
        getAdminFee();
      }
    });

    onMounted(() => {
      if (donation.value) {
        loading.value = true;
        try {
          getAdminFee();
          if (
            donation.value.paymentSpecification &&
            donation.value.paymentSpecification.paymentMethod
          ) {
            if (isInvoicePayment(donation.value.paymentSpecification.paymentMethod)) {
              paymentText.value = t('confirm.invoice').toString();
            } else if (isPayLinkPayment(donation.value.paymentSpecification.paymentMethod)) {
              mobileNumber.value = donation.value.donor.mobileNumber;
              paymentText.value = t('confirm.paylink').toString();
            } else {
              paymentText.value = t('summary.proceedToPayment').toString();
            }
          }
          numberOfPaymentAttempts.value = 0;
        } finally {
          loading.value = false;
        }
      }
    });

    return {
      t,
      rules,
      form,
      redirecting,
      loading,
      valid,
      failed,
      errorText,
      invoiceEmail,
      mobileNumber,
      paymentText,
      sms,
      paymentLoopBreakerThreshold,
      numberOfPaymentAttempts,
      adminPercentage,
      amountValue,
      steps,
      currentCurrency,
      donationSettings,
      requestSession,
      donation,
      isEmailInvoice,
      nextText,
      mobileLabel,
      isPhoneBased,
      donationAmountValue,
      adminAmount,
      totalAmountCalculated,
      formatCurrency,
      paymentMethodsForSelect,
      reset,
      goToPaymentMethodSelection,
      goToSuccessMethodSelection,
      isInvoicePayment,
      isPayLink,
      validateDonationAmount,
      getAdminFee,
      fetchPaymentMethods,
      goToPayment,
      privacyPolicyUrl,
      termsUrl,
      PaymentType,
    };
  },
});
</script>

<style scoped>
.donation-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  margin-top: 5px;
}
.donation-summary__label {
  font-weight: 700;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
}
.donation-summary__value {
  font-weight: 400;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
  line-height: 155%;
}
</style>
